// Single source of truth for app navigation.
//
// Consumed identically by three surfaces: Sidebar, the ⌘K CommandPalette, and the
// "Minha tela inicial" select in Settings. Pure data + pure helpers — no React, no I/O.
//
// See .planning/phases/14-reforma-menu-rotina-busca-home/14-CONTEXT.md (Árvore-alvo final)
// and 14-RESEARCH.md (Pattern 1: single catalog eliminates divergent screen lists).
import {
  LayoutDashboard,
  Bell,
  Gauge,
  Activity,
  Route,
  Fuel,
  FileText,
  ClipboardList,
  Layers,
  Cpu,
  Cog,
  MapPin,
  Waypoints,
  Bot,
  UserCog,
  Users,
  Shield,
  Settings,
} from 'lucide-react';

// NAV_SECTIONS — the full nav tree (4 top-level sections). Routes are EN, labels PT-BR
// (UI-SPEC Copywriting Contract). `gate` (when present) is a permission string consumed by
// `canSee`: either the literal 'admin' or a hasPermission() key (e.g. 'trip:read').
export const NAV_SECTIONS = [
  {
    id: 'acompanhar',
    label: 'ACOMPANHAR',
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, keywords: ['mapa', 'frota', 'painel'] },
      { path: '/alerts', label: 'Alertas', icon: Bell, keywords: ['alarme', 'geofence', 'notificações'] },
    ],
  },
  {
    id: 'operar',
    label: 'OPERAR',
    groups: [
      {
        id: 'operations',
        label: 'Operações',
        icon: Gauge,
        items: [
          { path: '/operations/activity', label: 'Atividade da Operação', icon: Activity, keywords: ['atividade', 'regime'] },
          { path: '/operations/trips', label: 'Viagens', icon: Route, gate: 'trip:read', keywords: ['viagem', 'despacho'] },
          { path: '/operations/fuel-control', label: 'Controle de Combustível', icon: Gauge, keywords: ['abastecimento', 'tanque', 'saldo', 'combustível'] },
          { path: '/operations/reports', label: 'Relatórios de Viagens', icon: FileText, gate: 'trip:read', keywords: ['relatório', 'pdf', 'export'] },
          { path: '/operations/fuel-audit', label: 'Análise de Combustível', icon: Fuel, keywords: ['auditoria', 'nfe', 'defasagem'] },
        ],
      },
    ],
  },
  {
    id: 'cadastros',
    label: 'CADASTROS',
    groups: [
      {
        id: 'equipamentos',
        label: 'Equipamentos',
        icon: ClipboardList,
        items: [
          { path: '/equipments', label: 'Equipamentos', icon: ClipboardList, keywords: ['frota', 'embarcação', 'draga'] },
          { path: '/equipment-groups', label: 'Grupos', icon: Layers, keywords: ['grupo'] },
          { path: '/equipment-types', label: 'Tipos', icon: Cpu, gate: 'admin', keywords: ['tipo'] },
          { path: '/engine-models', label: 'Modelos de Motor', icon: Cog, keywords: ['motor', 'curva', 'load curve', 'catálogo'] },
        ],
      },
      {
        id: 'geografia',
        label: 'Geografia',
        icon: MapPin,
        items: [
          { path: '/geofences', label: 'Geofences', icon: MapPin, keywords: ['zona', 'polígono', 'baliza'] },
          { path: '/routes', label: 'Rotas', icon: Waypoints, keywords: ['rota', 'trajeto'] },
        ],
      },
    ],
  },
  {
    id: 'sistema',
    label: 'SISTEMA',
    items: [
      { path: '/ai-helper', label: 'AI Helper', icon: Bot, keywords: ['ia', 'assistente', 'ajuda'] },
    ],
    groups: [
      {
        id: 'admin',
        label: 'Administração',
        icon: UserCog,
        gate: 'admin',
        items: [
          { path: '/users', label: 'Usuários', icon: Users, keywords: ['usuário', 'conta'] },
          { path: '/access-control', label: 'Controle de Acesso', icon: Shield, keywords: ['rbac', 'permissão', 'papel'] },
          { path: '/settings', label: 'Configurações', icon: Settings, keywords: ['config', 'preferência', 'idioma', 'boletim', 'monitoramento'] },
        ],
      },
    ],
  },
];

// NAV_ACTIONS — the ⌘K global quick-actions (D-07/D-08). `contextual`/`contextualLabel`/
// `contextualPath` let the palette adapt the action to the currently-focused equipment
// (e.g. deep-link a refuel straight to that equipment's tank).
export const NAV_ACTIONS = [
  {
    id: 'refuel',
    label: 'Lançar abastecimento',
    path: '/operations/fuel-control?action=refuel',
    icon: Fuel,
    keywords: ['abastecer', 'combustível', 'tanque'],
    contextual: (eq) => !!(eq && eq.uses_fuel),
    contextualLabel: (eq) => `Lançar abastecimento no ${eq.tag}`,
    contextualPath: (eq) => `/operations/fuel-control?equipment=${encodeURIComponent(eq.id)}&action=refuel`,
  },
  {
    id: 'new-trip',
    label: 'Nova viagem',
    path: '/operations/trips?action=new',
    icon: Route,
    gate: 'trip:read',
    keywords: ['viagem', 'despacho', 'nova'],
  },
  {
    id: 'new-geofence',
    label: 'Criar geofence',
    path: '/geofences?action=new',
    icon: MapPin,
    keywords: ['geofence', 'zona', 'criar'],
  },
];

// canSee — the single gate resolver (D-03). Imported identically by Sidebar, CommandPalette
// and SettingsPage so a gated item can never leak through one surface but not another.
// Gate is UX only; real authority stays in the backend (ProtectedRoute + endpoint RBAC).
export const canSee = (item, { isAdmin, hasPermission }) =>
  !item.gate || (item.gate === 'admin' ? isAdmin : hasPermission(item.gate));

// flattenItems — walks NAV_SECTIONS and returns every leaf navigable screen (from both
// section.items and section.groups[].items) as a flat array. Internal only — not exported.
//
// Group-level gates (e.g. the 'admin' group wrapping Usuários/Controle de Acesso/
// Configurações, none of which carry their own `gate`) are propagated onto each item that
// doesn't already declare its own gate. Without this, flattening would silently drop the
// group's gate and canSee would treat those items as ungated — a gate leak that violates
// the single-resolver guarantee (threat T-14-02).
function flattenItems() {
  const flat = [];
  for (const section of NAV_SECTIONS) {
    if (section.items) flat.push(...section.items);
    if (section.groups) {
      for (const group of section.groups) {
        if (group.items) {
          for (const item of group.items) {
            flat.push(item.gate ? item : { ...item, gate: group.gate });
          }
        }
      }
    }
  }
  return flat;
}

// getHomeOptions — canSee-filtered flat list of navigable screens for the "Tela ao entrar"
// select (D-11/D-12). Actions (NAV_ACTIONS) are NOT included — only navigable screens.
export const getHomeOptions = ({ isAdmin, hasPermission }) =>
  flattenItems()
    .filter((item) => canSee(item, { isAdmin, hasPermission }))
    .map((item) => ({ path: item.path, label: item.label }));

// HOME_ALLOWLIST — every valid home-page target, including Dashboard ('/') itself.
// Built once at module load; used by sanitizeHome to reject anything not in the catalog.
const HOME_ALLOWLIST = new Set(['/', ...flattenItems().map((item) => item.path)]);

// sanitizeHome — anti open-redirect guard (Security V5, D-12 fallback). Returns `value`
// only when it is an internal, allowlisted path; otherwise returns null so callers can
// degrade gracefully via `sanitizeHome(user?.home_page) || '/'`.
export const sanitizeHome = (value) => {
  if (typeof value !== 'string') return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null;
  if (!HOME_ALLOWLIST.has(value)) return null;
  return value;
};
